import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const TradingPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-tev-green-bright to-tev-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">TRADING</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
              A Focus on Exports - Connecting Africa's Agricultural Potential with Global Markets
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants} className="relative order-2 md:order-1">
              <img 
                src="/images/Loading Screen Truck.jpeg" 
                alt="Export Trading Operations" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-tev-green/60 to-transparent rounded-2xl"></div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="order-1 md:order-2 bg-gradient-to-br from-tev-green-light to-tev-green-bright p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-tev-green text-white p-4 rounded-full mr-4">
                  <i className="fas fa-globe text-2xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white">A FOCUS ON EXPORTS</h3>
              </div>
              <p className="text-lg text-white leading-relaxed mb-6">
                We are committed to delivering high-quality agricultural products to global markets. With a strong export focus, we work 
                across the value chain to source, process, and supply premium-grade commodities that meet the demanding standards of 
                international buyers. Our operations emphasize quality assurance, traceability, and compliance, ensuring every product—
                from beans and groundnuts to cassava-based goods—meets export requirements and arrives in global markets in its best 
                form.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border border-tev-green">
                  <div className="flex items-center mb-3">
                    <i className="fas fa-shipping-fast text-tev-green text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Global Markets</h4>
                  </div>
                  <p className="text-gray-700">Premium-grade commodities worldwide</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-lg border border-tev-green-light">
                  <div className="flex items-center mb-3">
                    <i className="fas fa-certificate text-tev-green-light text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Quality Standards</h4>
                  </div>
                  <p className="text-gray-700">Meeting international requirements</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TEV Foods Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">TEV FOODS EXPORT VISION</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bg-gradient-to-br from-tev-green-light to-tev-green-bright p-8 rounded-2xl shadow-xl"
          >
            <div className="flex items-center mb-6">
              <div className="bg-tev-green text-white p-4 rounded-full mr-4">
                <i className="fas fa-rocket text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white">Connecting Africa's Agricultural Potential</h3>
            </div>
            <p className="text-lg text-white leading-relaxed mb-8">
              By investing in modern processing, strong farmer partnerships, and efficient logistics, TEV Foods aspires to connect Africa's
              agricultural potential with international demand. Our export-driven approach not only opens new market opportunities but also 
              boosts incomes for farmers, strengthens value chains, and promotes the global recognition of Africa's finest agricultural 
              products.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-tev-green">
                <div className="text-center mb-4">
                  <div className="bg-tev-green text-white p-4 rounded-full w-16 h-16 mx-auto mb-3">
                    <i className="fas fa-users text-xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900">Farmer Income Growth</h4>
                </div>
                <p className="text-gray-700 text-center">Boosting farmer incomes through export opportunities</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg border border-tev-green-light">
                <div className="text-center mb-4">
                  <div className="bg-tev-green-light text-white p-4 rounded-full w-16 h-16 mx-auto mb-3">
                    <i className="fas fa-link text-xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900">Strong Value Chains</h4>
                </div>
                <p className="text-gray-700 text-center">Strengthening agricultural value chains</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg border border-tev-green-bright">
                <div className="text-center mb-4">
                  <div className="bg-tev-green-bright text-white p-4 rounded-full w-16 h-16 mx-auto mb-3">
                    <i className="fas fa-award text-xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900">Global Recognition</h4>
                </div>
                <p className="text-gray-700 text-center">Promoting Africa's finest agricultural products globally</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Offering */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">OUR PRODUCT OFFERING</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-12"
          >
            {/* Semi-processed Products */}
            <div>
              <motion.div 
                variants={itemVariants}
                className="text-center mb-8"
              >
                <div className="inline-flex items-center bg-gradient-to-r from-tev-green to-tev-green-light px-6 py-3 rounded-full shadow-lg">
                  <i className="fas fa-seedling text-white text-xl mr-3"></i>
                  <h3 className="text-2xl font-bold text-white">Semi-processed Products</h3>
                </div>
              </motion.div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-center mb-4">
                    <img 
                      src="/images/beans.jpg" 
                      alt="Common Beans" 
                      className="w-full h-32 object-cover rounded-xl mb-4"
                    />
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-tev-green text-white p-3 rounded-full mr-3">
                      <i className="fas fa-bean text-xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">Common Beans</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-tev-green-light p-3 rounded-lg text-center">
                      <p className="text-white font-semibold">Red speckled</p>
                    </div>
                    <div className="bg-tev-green p-3 rounded-lg text-center">
                      <p className="text-white font-semibold">NUA 45</p>
                    </div>
                    <div className="bg-tev-green-bright p-3 rounded-lg text-center">
                      <p className="text-white font-semibold">White haricot</p>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-center mb-4">
                    <img 
                      src="/images/raw.jpg" 
                      alt="Raw Peanut Kernels" 
                      className="w-full h-32 object-cover rounded-xl mb-4"
                    />
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-amber-600 text-white p-3 rounded-full mr-3">
                      <i className="fas fa-peanut-hot text-xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">Raw Peanut Kernels</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-amber-100 p-3 rounded-lg text-center">
                      <p className="text-amber-700 font-semibold">Chalimbana</p>
                    </div>
                    <div className="bg-amber-200 p-3 rounded-lg text-center">
                      <p className="text-amber-700 font-semibold">CG9</p>
                    </div>
                    <div className="bg-amber-300 p-3 rounded-lg text-center">
                      <p className="text-amber-700 font-semibold">CG10</p>
                    </div>
                    <div className="bg-amber-400 p-3 rounded-lg text-center">
                      <p className="text-amber-700 font-semibold">Others</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
            
            {/* Value Added Products */}
            <div>
              <motion.div 
                variants={itemVariants}
                className="text-center mb-8"
              >
                <div className="inline-flex items-center bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 rounded-full shadow-lg">
                  <i className="fas fa-cube text-white text-xl mr-3"></i>
                  <h3 className="text-2xl font-bold text-white">Value Added Products</h3>
                </div>
              </motion.div>
              
              <div className="grid md:grid-cols-3 gap-8">
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-center">
                    <img 
                      src="/images/ground 2.jpg" 
                      alt="Blanched Nuts" 
                      className="w-full h-32 object-cover rounded-xl mb-4"
                    />
                    <div className="bg-gradient-to-r from-amber-100 to-amber-200 p-6 rounded-full w-20 h-20 mx-auto mb-4">
                      <i className="fas fa-fire text-amber-600 text-2xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Blanched Nuts</h4>
                    <p className="text-gray-600">Premium quality blanched peanuts</p>
                  </div>
                </motion.div>
                
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-center">
                    <img 
                      src="/images/peanut 1.jpg" 
                      alt="Roasted Nuts" 
                      className="w-full h-32 object-cover rounded-xl mb-4"
                    />
                    <div className="bg-gradient-to-r from-orange-100 to-orange-200 p-6 rounded-full w-20 h-20 mx-auto mb-4">
                      <i className="fas fa-temperature-high text-orange-600 text-2xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Roasted Nuts</h4>
                    <p className="text-gray-600">Perfectly roasted for optimal flavor</p>
                  </div>
                </motion.div>
                
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-center">
                    <img 
                      src="/images/nice.jpg" 
                      alt="Peanut Butter" 
                      className="w-full h-32 object-cover rounded-xl mb-4"
                    />
                    <div className="bg-gradient-to-r from-amber-100 to-amber-200 p-6 rounded-full w-20 h-20 mx-auto mb-4">
                      <i className="fas fa-blender text-amber-600 text-2xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Peanut Butter</h4>
                    <p className="text-gray-600">Smooth and creamy peanut butter</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TradingPage;
